import { capitalize } from "@gettactic/helpers/src/strings/capitalize";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Underline
} from "lucide-react";
import React, { useMemo } from "react";
import { useCustomParkingVerbiageOnMapBuilder as useCustomParkingVerbiage } from "../../../../../../hooks/useCustomParkingVerbiageOnMapBuilder";
import {
  ShapeTextAlignment,
  SpotAppearanceToLabel,
  SpotShape,
  TextShape
} from "../../../../../../types";
import { ColorPickerInput } from "../../../../../ui/ColorPickerInput";
import { EditorField } from "../../../../../ui/EditorField";
import { EditorSelect } from "../../../../../ui/EditorSelect";
import { IconButton } from "../../../../../ui/IconButton";
import { PanelSection } from "../../../../../ui/PanelShell";
import { Seg } from "../../../../../ui/Seg";
import { useMapEditorActions } from "../../../../contexts/MapEditorContext";
import {
  DEFAULT_NODE_LABEL_ALIGNMENT,
  DEFAULT_NODE_RECT_COLOR
} from "../../../../defines";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { ShapeSectionOptions } from "../ShapePanel";

export const CommonTextPanelControls: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape, disableHistoryRecording, enableHistoryRecording } =
    useMapEditorActions();
  const shape = shapes[0];
  const textShape = shape as TextShape;

  const isNodeType = shape.type === "spot";

  const handleChangeShapeText = (value: string) => {
    if (shape.type === "spot") {
      shapes.forEach((shape) => {
        updateShape(shape.id, {
          label: value
        });
      });
      return;
    }
    shapes.forEach((shape) => {
      updateShape(shape.id, { text: value });
    });
  };

  const handleChangeShapeName = (name: string) => {
    if (shape.type === "spot") {
      shapes.forEach((shape) => {
        updateShape(shape.id, {
          name
        });
      });
      return;
    }
    shapes.forEach((shape) => {
      updateShape(shape.id, { name });
    });
  };

  const handleChangeShapeTextColor = (value: string) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { textColor: value });
    });
  };

  const handleSetBold = (value: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isBold: value });
    });
  };

  const handleSetUnderline = (value: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isUnderline: value });
    });
  };

  const handleSetItalic = (value: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isItalic: value });
    });
  };

  const handleFontSizeChange = (value: string) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { fontSize: Number(value) });
    });
  };

  const handleLabelTextAlignChange = (textAlign: ShapeTextAlignment) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { textAlign });
    });
  };

  const shapeWithText = shape as TextShape;

  const spotShape = shape as SpotShape;
  const text = spotShape.type === "spot" ? spotShape.label : shapeWithText.text;
  const name = spotShape.type === "spot" ? spotShape.name : "";

  const debouncedText = useDebouncedValue(text || "", handleChangeShapeText);
  const debouncedName = useDebouncedValue(name || "", handleChangeShapeName);

  const handleTextFocus = () => {
    disableHistoryRecording();
  };

  const handleTextBlur = (flush: () => void) => {
    flush();
    enableHistoryRecording();
  };

  const textAlignment =
    shape.type === "spot"
      ? shapeWithText.textAlign || DEFAULT_NODE_LABEL_ALIGNMENT
      : shapeWithText.textAlign;

  // Label/title text is about the resource's own type identity (so parking
  // spots keep saying "Parking Group"/customParkingVerbiage even when their
  // Default appearance renders like Node) — not the rendered look, so this
  // intentionally uses the raw type fallback rather than
  // getEffectiveSpotAppearance. Matches the sibling spot panel's
  // spotType-based title (SharedSpotPanelControls).
  const spotAppearance =
    (shape as SpotShape).spotAppearance || (shape as SpotShape).spotType;

  const customParkingVerbiage = useCustomParkingVerbiage();
  const spotTitle = useMemo(() => {
    if (shape.type !== "spot") return "";

    const spotIsParking =
      spotAppearance === "parking_lot" || spotAppearance === "parking_space";

    let title = capitalize(SpotAppearanceToLabel[spotAppearance] || "Resource");
    if (spotIsParking && customParkingVerbiage !== null) {
      title = `${capitalize(customParkingVerbiage)} Group`;
    }
    return title;
  }, [shape.type, customParkingVerbiage, shape]);

  const textField = (
    <div className="flex flex-col gap-1">
      <div className="text-neutral-600 text-xs">
        {isNodeType ? `${spotTitle} Label` : "Text"}
      </div>
      <textarea
        aria-label={isNodeType ? `${spotTitle} Label` : "Text"}
        className="h-10 w-full rounded-md border border-neutral-200 bg-background-primary p-2 text-neutral-900 text-xs outline-none transition-colors hover:border-neutral-300 focus:border-blue-500"
        value={debouncedText.value}
        onChange={(e) => debouncedText.onChange(e.target.value)}
        onFocus={handleTextFocus}
        onBlur={() => handleTextBlur(debouncedText.flush)}
      />
    </div>
  );

  return (
    <PanelSection label={isNodeType ? undefined : "Text"}>
      <div className="flex flex-col gap-3">
        {isNodeType ? (
          <>
            {textField}
            <div className="flex flex-col gap-1">
              <div className="text-neutral-600 text-xs">
                {capitalize(spotTitle)} Name
              </div>
              <EditorField
                label={`${capitalize(spotTitle)} Name`}
                value={debouncedName.value}
                onChange={(e) => debouncedName.onChange(e.target.value)}
                onFocus={handleTextFocus}
                onBlur={() => handleTextBlur(debouncedName.flush)}
              />
            </div>
          </>
        ) : (
          textField
        )}
        <div className="flex flex-col gap-1">
          <div className="text-neutral-600 text-xs">
            {isNodeType ? `${capitalize(spotTitle)} Label color` : "Text color"}
          </div>
          <ColorPickerInput
            value={textShape.textColor || DEFAULT_NODE_RECT_COLOR}
            onChange={handleChangeShapeTextColor}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <IconButton
              size="sm"
              label="Bold"
              isActive={!!shapeWithText.isBold}
              onClick={() => handleSetBold(!shapeWithText.isBold)}
            >
              <Bold className="h-4 w-4" />
            </IconButton>
            <IconButton
              size="sm"
              label="Underline"
              isActive={!!shapeWithText.isUnderline}
              onClick={() => handleSetUnderline(!shapeWithText.isUnderline)}
            >
              <Underline className="h-4 w-4" />
            </IconButton>
            <IconButton
              size="sm"
              label="Italic"
              isActive={!!shapeWithText.isItalic}
              onClick={() => handleSetItalic(!shapeWithText.isItalic)}
            >
              <Italic className="h-4 w-4" />
            </IconButton>
          </div>
          <Seg<ShapeTextAlignment | "">
            label="Text alignment"
            className="flex-1"
            value={textAlignment || ""}
            onValueChange={(value) => {
              if (!value) return;
              handleLabelTextAlignChange(value);
            }}
            options={[
              {
                value: "left",
                ariaLabel: "Align left",
                label: <AlignLeft className="h-4 w-4" />
              },
              {
                value: "center",
                ariaLabel: "Align center",
                label: <AlignCenter className="h-4 w-4" />
              },
              {
                value: "right",
                ariaLabel: "Align right",
                label: <AlignRight className="h-4 w-4" />
              }
            ]}
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-neutral-600 text-xs">Font size</div>
          <EditorSelect
            label="Font size"
            value={String((shape as TextShape).fontSize || 20)}
            onValueChange={handleFontSizeChange}
            options={[
              12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72
            ].map((size) => ({ value: String(size), label: `${size}px` }))}
          />
        </div>
      </div>
    </PanelSection>
  );
};
